import type { AlgebraKey } from './keys';

export const en: Record<AlgebraKey, string> = {
  'app.title': 'Algebra',
  'app.eyebrow': 'Math · Algebra',
  'app.tagline': 'Solve, factor, simplify, and solve.',
  'app.panel.heading': 'Algebra',
  'panel.experiment': 'Experiment',
  'panel.enter-number': 'Enter an expression or equation',
  'panel.show-sigma-stack': 'Show partial-sum stack',
  'panel.show-closed-form': 'Show closed form',
  'panel.show-sign-chart': 'Show sign chart',
  'label.system': 'System',
  'label.solution': 'Solution',
  'label.steps': 'Steps',
  'hint.line1': 'Pick an experiment, manipulate the expressions, watch the algebra change.',
  'hint.line2': 'Educational content lives in the rules of algebra, not the text.',
  // 8 Experiments
  'experiment.equation-balance.label': 'Equation balance',
  'experiment.equation-balance.info': 'Solve equations by balancing both sides',
  'experiment.factor-tiles.label': 'Factor tiles',
  'experiment.factor-tiles.info': 'Factor polynomials by arranging tiles',
  'experiment.quadratic-workshop.label': 'Quadratic workshop',
  'experiment.quadratic-workshop.info':
    'Solve quadratics three ways: factoring, completing the square, and the formula',
  'experiment.polynomial-arithmetic.label': 'Polynomial arithmetic',
  'experiment.polynomial-arithmetic.info': 'Multiply and divide polynomials',
  'experiment.systems-laboratory.label': 'Systems laboratory',
  'experiment.systems-laboratory.info': 'Solve systems of linear equations',
  'experiment.inequality-line.label': 'Inequality line',
  'experiment.inequality-line.info': 'Solve and visualize inequalities on a number line',
  'experiment.sequences-staircase.label': 'Sequences staircase',
  'experiment.sequences-staircase.info': 'Explore arithmetic and geometric sequences',
  'experiment.expression-rewriter.label': 'Expression rewriter',
  'experiment.expression-rewriter.info': 'Apply algebraic rules to simplify expressions',
  // Operations
  'op.add-both-sides': 'Add to both sides',
  'op.subtract-both-sides': 'Subtract from both sides',
  'op.multiply-both': 'Multiply both sides',
  'op.divide-both': 'Divide both sides',
  'op.square-both': 'Square both sides',
  'op.cross-multiply': 'Cross multiply',
  // Methods
  'method.factoring': 'Factoring',
  'method.completing-square': 'Completing the square',
  'method.quadratic-formula': 'Quadratic formula',
  'method.substitution': 'Substitution',
  'method.elimination': 'Elimination',
  'method.rational-root-theorem': 'Rational root theorem',
  'method.special-products': 'Special products',
  // Rewriter rules
  'rule.distribute': 'Distribute',
  'rule.collect-like-terms': 'Collect like terms',
  'rule.factor-common-monomial': 'Factor out common monomial',
  'rule.cancel-common-factor': 'Cancel common factor',
  'rule.simplify-fraction': 'Simplify fraction',
  'rule.flatten-add': 'Flatten addition',
  'rule.flatten-mul': 'Flatten multiplication',
  'rule.exponent-product': 'Product of exponents',
  'rule.exponent-power': 'Power of a power',
  'rule.exponent-quotient': 'Quotient of exponents',
  'rule.negative-exponent': 'Negative exponent',
  'rule.zero-exponent': 'Zero exponent',
  'rule.log-product': 'Logarithm product rule',
  'rule.log-power': 'Logarithm power rule',
  'rule.log-quotient': 'Logarithm quotient rule',
  'rule.change-of-base': 'Change of base',
  'rule.radical-to-frac-exp': 'Radical to fractional exponent',
  'rule.frac-exp-to-radical': 'Fractional exponent to radical',
  'rule.rationalize-denominator': 'Rationalize denominator',
  'rule.multiply-by-conjugate': 'Multiply by conjugate',
  // Solution kinds
  'kind.unique': 'Unique solution',
  'kind.inconsistent': 'Inconsistent',
  'kind.dependent': 'Dependent',
  'kind.extraneous': 'Extraneous',
  'kind.consistent-unique': 'Consistent and unique',
  'kind.no-real-roots': 'No real roots',
  'kind.complex-pair': 'Complex pair',
  'systems.no-solution': 'No solution (inconsistent)',
  'systems.infinitely-many': 'Infinitely many (dependent)',
  // Sequence classes
  'class.arithmetic': 'Arithmetic',
  'class.geometric': 'Geometric',
  'class.recurrence': 'Recurrence',
  'class.convergent': 'Convergent',
  'class.divergent': 'Divergent',
  'class.telescoping': 'Telescoping',
  // Steps
  'step.add': 'Add',
  'step.subtract': 'Subtract',
  'step.multiply': 'Multiply',
  'step.divide': 'Divide',
  'step.distribute': 'Distribute',
  'step.collect-like-terms': 'Collect like terms',
  'step.cross-multiply': 'Cross multiply',
  'step.square-both': 'Square both sides',
  'step.factor': 'Factor',
  'step.quadratic-formula': 'Apply quadratic formula',
  'step.isolate-variable': 'Isolate variable',
  'step.extraneous-root-removed': 'Extraneous root removed',
  'step.factor-gcd': 'Factor out GCD',
  'step.factor-rational-root': 'Factor using rational root',
  'step.factor-grouping': 'Factor by grouping',
  'step.factor-difference-of-squares': 'Factor as difference of squares',
  'step.factor-sum-of-cubes': 'Factor as sum of cubes',
  'step.factor-difference-of-cubes': 'Factor as difference of cubes',
  'step.factor-perfect-square': 'Factor as perfect square',
  'step.factor-special-product': 'Factor using special product',
  'step.factor-quadratic-formula': 'Factor using quadratic formula',
  'step.factor-irreducible': 'Irreducible over rationals',
  'step.system-substitute': 'Substitute',
  'step.system-row-swap': 'Swap rows',
  'step.system-row-scale': 'Scale row',
  'step.system-row-add': 'Add row to row',
  'step.inequality-move-to-one-side': 'Move to one side',
  'step.inequality-factor': 'Factor',
  'step.inequality-critical-points': 'Find critical points',
  'step.inequality-asymptotes': 'Find asymptotes',
  'step.inequality-sign-chart': 'Build sign chart',
  'step.inequality-read-intervals': 'Read solution intervals',
  // Buttons
  'btn.reset': 'Reset',
  'btn.reset-view': 'Reset view',
  'btn.randomise': 'Randomise',
  'btn.advanced': 'Advanced',
  'btn.auto-arrange': 'Auto-arrange',
  'btn.check-equivalent': 'Check equivalent',
  'btn.undo': 'Undo',
  'btn.swap': 'Swap P ↔ Q',
  // Messages
  'message.parse-error.unexpected-token': 'Unexpected token',
  'message.parse-error.unknown-variable': 'Unknown variable',
  'message.parse-error.not-a-polynomial': 'Not a polynomial',
  'message.no-real-solutions': 'No real solutions',
  'message.extraneous-root-removed': 'Extraneous root removed',
  'message.dividing-by-zero-blocked': 'Cannot divide by zero',
  'message.target-form-reached': 'Target form reached',
  // Equation balance
  'balance.solved': 'Solved!',
  'balance.not-tileable':
    'Tiles need small whole-number coefficients — edit the equation or randomise.',
  // Factor tiles headline
  'factor.irreducible': 'irreducible over ℚ',
  'special.difference-of-squares': 'Special product: difference of squares',
  'special.perfect-square': 'Special product: perfect square',
  'special.sum-of-cubes': 'Special product: sum of cubes',
  'special.difference-of-cubes': 'Special product: difference of cubes',
};
